import Abstract from "../../abstract-view/abstract.js";

import {
  createElement
} from "./../../render/render.js";

import {
  getSizes
} from "../../../utils/get-sizes.js";

import {
  CLOSE_BUTTON_TEXT,
  SOUND_BUTTON_TEXT
} from "../lexicon.js";

const createVideoTemplate = (options) => {
  const caption = (
    options &&
    options.text
  ) ?
    `<p class="promo-video__caption">${options.text}</p>` :
    ``;

  const srcMp4 = (
    options &&
    options.src &&
    options.src.mp4
  ) ?
    `<source src="${options.src.mp4}" type="video/mp4">` :
    ``;

  const preload = (
    options &&
    options.isMobile === true
  ) ?
    ` preload="none"` :
    ``;

  const autoplay = (
    options &&
    options.isMobile === true
  ) ?
    `` :
    ` autoplay`;

  const poster = (
    options &&
    options.poster
  ) ?
    ` poster="${options.poster}"` :
    ``;

  const backgroundImage = (
    options &&
    options.poster
  ) ?
    `background-image:url(${options.poster});` :
    ``;

  return (
    `<section class="promo-video" style="display: none;" tabindex="-1">
      ${caption}
      <div class="promo-video-wrapper" style="display: none;${backgroundImage}">
        <video class="promo-video__video"${autoplay} muted loop playsinline${preload}${poster}>
          ${srcMp4}
        </video>
        <p class="promo-video__elapsed-time"></p>
        <button class="promo-video__button promo-video__button_sound promo-video__button_sound_mute">
          <span class="visually-hidden">${SOUND_BUTTON_TEXT}</span>
        </button>
        <button class="promo-video__button promo-video__button_close">
          <span class="visually-hidden">${CLOSE_BUTTON_TEXT}</span>
        </button>
      </div>
    </section>`
  );
};


export default class Video extends Abstract {
  constructor(options) {
    super();

    this._videoElement = null;
    this._soundElement = null;
    this._closeElement = null;
    this._elapsedElement = null;
    this._closeElement = null;
    this._wrapperElement = null;
    this._options = options;

    this._canplayHandler = this._canplayHandler.bind(this);
    this._clickHandler = this._clickHandler.bind(this);
    this._soundClickHandler = this._soundClickHandler.bind(this);
    this._timeUpdateHandler = this._timeUpdateHandler.bind(this);
    this._closeClickHandler = this._closeClickHandler.bind(this);
  }

  getElement() {
    if (this._element === null) {
      this._element = createElement(this._getTemplate());
    }

    return this._element;
  }

  getWrapperElement() {
    if (!this._wrapperElement) {
      this._wrapperElement = this._element.querySelector(`.promo-video-wrapper`);
    }

    return this._wrapperElement;
  }

  getVideoElement() {
    if (!this._videoElement) {
      this._videoElement = this._element.querySelector(`video`);
    }

    return this._videoElement;
  }

  getSoundElement() {
    if (!this._soundElement) {
      this._soundElement = this._element.querySelector(`.promo-video__button_sound`);
    }

    return this._soundElement;
  }

  getCloseElement() {
    if (!this._closeElement) {
      this._closeElement = this._element.querySelector(`.promo-video__button_close`);
    }

    return this._closeElement;
  }

  getElapsedElement() {
    if (!this._elapsedElement) {
      this._elapsedElement = this._element.querySelector(`.promo-video__elapsed-time`);
    }

    return this._elapsedElement;
  }

  setWrapperVisible() {
    this.getWrapperElement().style.display = null;
  }

  setRendered() {
    setTimeout(() => {
      this.getElement().classList.add(`${this.getElement().classList[0]}_rendered`);
    }, 1000);
  }

  expand(isMobile) {
    let height;

    if (isMobile === false) {
      const sizes = getSizes(this.getElement());
      const width = window.outerWidth / 2 - parseFloat(sizes.left.replace(/[^\d]/g, ``));
      height = width * 9 / 16;
    }

    this.getElement().classList.add(`${this.getElement().classList[0]}_expanded`);

    if (height && height > 0) {
      this.getElement().style.height = `${height}px`;
    }
  }

  collapse() {
    this.getElement().style.height = null;
    this.getElement().classList.remove(`${this.getElement().classList[0]}_expanded`);
  }

  play() {
    this.getVideoElement().play();
  }

  pause() {
    this.getVideoElement().pause();
  }

  mute() {
    this.getVideoElement().muted = true;
    this.getSoundElement().classList.add(`promo-video__button_sound_mute`);
  }

  unmute() {
    this.getVideoElement().muted = false;
    this.getSoundElement().classList.remove(`promo-video__button_sound_mute`);
  }

  setOnceStarted() {
    this.getElement().classList.add(`${this.getElement().classList[0]}_started`);
  }

  setCloseClickHandler(callback) {
    this._callback.closeClick = callback;
    this.getCloseElement().addEventListener(`click`, this._closeClickHandler);
  }

  setElapsedTime(time) {
    this.getElapsedElement().textContent = time;
  }

  setZIndex(value) {
    const element = this.getElement();
    element.style.zIndex = value;
  }

  setWrapperZIndex(value) {
    this.getWrapperElement().style.zIndex = value;
  }

  removeWrapperZIndex() {
    this.getWrapperElement().style.zIndex = null;
  }

  setTimeUpdateHandler(callback) {
    this._callback.timeUpdate = callback;
    this.getVideoElement().addEventListener(`timeupdate`, this._timeUpdateHandler);
  }

  removeTimeUpdateHandler() {
    this.getVideoElement().removeEventListener(`timeupdate`, this._timeUpdateHandler);
    delete this._callback.timeUpdate;
  }

  setClickHandler(callback) {
    this._callback.click = callback;
    this.getElement().addEventListener(`click`, this._clickHandler);
  }

  setSoundClickHandler(callback) {
    this._callback.soundClick = callback;
    this.getSoundElement().addEventListener(`click`, this._soundClickHandler);
  }

  setCanplayHandler(callback) {
    this._callback.canplay = callback;
    this.getVideoElement().addEventListener(`canplay`, this._canplayHandler);
  }

  _getTemplate() {
    return createVideoTemplate(this._options);
  }

  _canplayHandler() {
    this._callback.canplay();
  }

  _clickHandler(evt) {
    this._callback.click(evt);
  }

  _soundClickHandler() {
    this._callback.soundClick();
  }

  _timeUpdateHandler() {
    this._callback.timeUpdate(
        this.getVideoElement().currentTime,
        this.getVideoElement().duration
    );
  }

  _closeClickHandler() {
    this._callback.closeClick();
  }
}
